// CHANGE-042: the partner's profile: logo, name, website, address, billing email. Not a set-up step: while the
// partner is still setting up, this page sends them Home.
import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { api, useRefreshMe } from '../../lib'
import { Loading } from '../../app/ui'
import { LogoEdit } from '../../app/logo'
import { Err, PartnerPage, useAction, useInvalidate, useSetup } from './common'

export function ProfilePage() {
  return <PartnerPage>{(pid, role) => <ProfileBody pid={pid} canEdit={role === 'owner' || role === 'admin'} />}</PartnerPage>
}

function ProfileBody({ pid, canEdit }: { pid: string; canEdit: boolean }) {
  const s = useSetup(pid)
  const inval = useInvalidate()
  const refreshMe = useRefreshMe()
  const empty = { name: '', website: '', line1: '', city: '', state: '', postal_code: '', billing_email: '' }
  const [f, setF] = useState(empty)
  useEffect(() => {
    const p = s.data?.partner
    if (!p) return
    setF({
      name: p.name, website: p.website ?? '', line1: p.address?.line1 ?? '', city: p.address?.city ?? '',
      state: p.address?.state ?? '', postal_code: p.address?.postal_code ?? '', billing_email: p.billing_email ?? '',
    })
  }, [s.data?.partner.id])
  const refresh = () => {
    inval()
    refreshMe()
  }
  const a = useAction(() => {
    const address = f.line1 || f.city || f.state || f.postal_code
      ? { line1: f.line1, city: f.city, state: f.state, postal_code: f.postal_code } : undefined
    return api('PATCH', `/api/partners/${pid}`, {
      name: f.name, website: f.website, address, billing_email: f.billing_email || undefined,
    })
  }, refreshMe)
  if (!s.data) return s.error ? <Err error={s.error} /> : <Loading />
  const p = s.data.partner
  if (p.status === 'draft') return <Navigate to="/partner" replace />
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value })
  return (
    <form className="page narrow stack" data-testid="partners-profile" onSubmit={(e) => { e.preventDefault(); a.run(undefined) }}>
      <h1>Profile</h1>
      <LogoEdit name={p.name} url={p.logo_url} path={`/api/partners/${pid}/logo`} canEdit={canEdit} onChange={refresh}
        testid="partners-logo" />
      <label>
        Name
        <input data-testid="partners-profile-name" value={f.name} onChange={set('name')} disabled={!canEdit} required />
      </label>
      <label>
        Website
        <input data-testid="partners-profile-website" value={f.website} onChange={set('website')} disabled={!canEdit} required />
      </label>
      <label>
        Street address
        <input data-testid="partners-profile-line1" value={f.line1} onChange={set('line1')} disabled={!canEdit} />
      </label>
      <div className="row">
        <input data-testid="partners-profile-city" aria-label="City" placeholder="City" style={{ flexGrow: 2 }} value={f.city} onChange={set('city')} disabled={!canEdit} />
        <input data-testid="partners-profile-state" aria-label="State" placeholder="State" value={f.state} onChange={set('state')} disabled={!canEdit} />
        <input data-testid="partners-profile-zip" aria-label="ZIP" placeholder="ZIP" value={f.postal_code} onChange={set('postal_code')} disabled={!canEdit} />
      </div>
      <label>
        Billing email
        <input data-testid="partners-profile-billing-email" type="email" value={f.billing_email} onChange={set('billing_email')} disabled={!canEdit} />
      </label>
      <Err error={a.error} />
      {canEdit && (
        <div className="row">
          <button className="primary" data-testid="partners-profile-save" disabled={a.busy}>Save</button>
          {a.done && <span className="muted" data-testid="partners-profile-saved">Saved.</span>}
        </div>
      )}
    </form>
  )
}
